// ============================================
// AlpenScreen Content Studio - Canvas Editor
// File: frontend/src/components/ContentStudio/CanvasEditor.js
// Fabric.js v6 compatible - With Professional Fonts
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas, Textbox, Rect, Circle, FabricImage } from 'fabric';
import {
  Save,
  Download,
  Undo,
  Redo,
  Type,
  Square,
  Circle as CircleIcon,
  Image,
  Trash2,
  Copy,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  ZoomIn,
  ZoomOut,
  Loader,
  Check,
  ImagePlus,
  Upload,
  Monitor,
  Smartphone,
  RectangleHorizontal,
  Tv
} from 'lucide-react';
import BackgroundGalleryModal from './BackgroundGalleryModal';
import AIAssistant from './AIAssistant';
import TranslationPanel from './TranslationPanel';
import AISettings from './AISettings';
import AIAssistantEnhanced from './AIAssistantEnhanced';
import TranslationPanelEnhanced from './TranslationPanelEnhanced';

// ============================================
// PROFESSIONAL FONT DEFINITIONS
// ============================================
const FONT_CATEGORIES = [
  {
    id: 'headlines',
    name: 'Headlines',
    nameKey: 'contentStudio.fonts.headlines',
    fonts: [
      { name: 'Montserrat', weights: ['400', '600', '700', '800', '900'] },
      { name: 'Poppins', weights: ['400', '600', '700', '800', '900'] },
      { name: 'Oswald', weights: ['400', '500', '600', '700'] },
      { name: 'Bebas Neue', weights: ['400'] },
      { name: 'Raleway', weights: ['400', '600', '700', '800', '900'] },
      { name: 'Anton', weights: ['400'] },
    ]
  },
  {
    id: 'body',
    name: 'Body Text',
    nameKey: 'contentStudio.fonts.body',
    fonts: [
      { name: 'Inter', weights: ['400', '500', '600', '700'] },
      { name: 'Lato', weights: ['400', '700', '900'] },
      { name: 'Roboto', weights: ['400', '500', '700', '900'] },
      { name: 'Open Sans', weights: ['400', '600', '700', '800'] },
      { name: 'Source Sans 3', weights: ['400', '600', '700', '900'] },
      { name: 'Nunito', weights: ['400', '600', '700', '800'] },
    ]
  },
  {
    id: 'elegant',
    name: 'Elegant',
    nameKey: 'contentStudio.fonts.elegant',
    fonts: [
      { name: 'Playfair Display', weights: ['400', '600', '700', '800', '900'] },
      { name: 'Lora', weights: ['400', '600', '700'] },
      { name: 'Cormorant Garamond', weights: ['400', '600', '700'] },
      { name: 'Merriweather', weights: ['400', '700', '900'] },
      { name: 'Crimson Text', weights: ['400', '600', '700'] },
    ]
  },
  {
    id: 'display',
    name: 'Display',
    nameKey: 'contentStudio.fonts.display',
    fonts: [
      { name: 'Abril Fatface', weights: ['400'] },
      { name: 'Righteous', weights: ['400'] },
      { name: 'Pacifico', weights: ['400'] },
      { name: 'Permanent Marker', weights: ['400'] },
      { name: 'Russo One', weights: ['400'] },
    ]
  },
  {
    id: 'swiss',
    name: 'Swiss Style',
    nameKey: 'contentStudio.fonts.swiss',
    fonts: [
      { name: 'DM Sans', weights: ['400', '500', '700'] },
      { name: 'Space Grotesk', weights: ['400', '500', '700'] },
      { name: 'Outfit', weights: ['400', '500', '600', '700'] },
      { name: 'Manrope', weights: ['400', '500', '600', '700', '800'] },
    ]
  }
];

// Flatten fonts for easy lookup
const ALL_FONTS = FONT_CATEGORIES.flatMap(cat => cat.fonts);

// Canvas format presets
const CANVAS_FORMATS = [
  { id: '16:9', name: 'landscape169', label: '16:9 Full HD', width: 1920, height: 1080, icon: Monitor, description: '1920×1080' },
  { id: '16:9-4k', name: 'landscape4k', label: '16:9 4K', width: 3840, height: 2160, icon: Tv, description: '3840×2160' },
  { id: '4:3', name: 'landscape43', label: '4:3 Landscape', width: 1440, height: 1080, icon: Monitor, description: '1440×1080' },
  { id: '9:16', name: 'portrait', label: '9:16 Portrait', width: 1080, height: 1920, icon: Smartphone, description: '1080×1920' },
  { id: '21:9', name: 'ultrawide', label: '21:9 Ultrawide', width: 2560, height: 1080, icon: RectangleHorizontal, description: '2560×1080' }
];

const CanvasEditor = ({ template, design, onSave, onClose }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const bgImageInputRef = useRef(null);

  // Determine initial format from template/design or default to 16:9
  const getInitialFormat = () => {
    const width = design?.width || template?.width;
    const height = design?.height || template?.height;
    
    if (width && height) {
      // Check for exact matches first
      const exactMatch = CANVAS_FORMATS.find(f => f.width === width && f.height === height);
      if (exactMatch) return exactMatch;
      
      // Check by ratio
      const ratio = width / height;
      if (Math.abs(ratio - 16/9) < 0.1) {
        return width > 2000 ? CANVAS_FORMATS[1] : CANVAS_FORMATS[0]; // 4K or Full HD
      }
      if (Math.abs(ratio - 4/3) < 0.1) return CANVAS_FORMATS[2];
      if (Math.abs(ratio - 9/16) < 0.1) return CANVAS_FORMATS[3];
      if (Math.abs(ratio - 21/9) < 0.1) return CANVAS_FORMATS[4];
    }
    return CANVAS_FORMATS[0]; // Default Full HD
  };

  // State
  const [canvas, setCanvas] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [designName, setDesignName] = useState(design?.name || template?.name || 'Neues Design');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [zoom, setZoom] = useState(0.5);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBgGalleryModal, setShowBgGalleryModal] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [showFontModal, setShowFontModal] = useState(false);
  const [bgColor, setBgColor] = useState('#1a1a2e');
  const [bgImage, setBgImage] = useState(null);
  const [canvasFormat, setCanvasFormat] = useState(getInitialFormat);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [bgAttribution, setBgAttribution] = useState(null);

  // ============================================
// DEBUG: Override canvas.add to find culprit
// ============================================
useEffect(() => {
  if (!canvas) return;
  
  const originalAdd = canvas.add;
  canvas.add = function(...objects) {
    objects.forEach(obj => {
      if (obj.type === 'image') {
        console.error('🚨 IMAGE ADDED TO CANVAS!');
        console.trace('Stack trace:');
      }
    });
    return originalAdd.call(this, ...objects);
  };
  
  return () => {
    canvas.add = originalAdd;
  };
}, [canvas]);

  // Load Google Fonts dynamically
  useEffect(() => {
    const loadFonts = async () => {
      // Build Google Fonts URL
      const fontFamilies = ALL_FONTS
        .map(font => {
          const weights = font.weights.join(';');
          return `family=${font.name.replace(/ /g, '+')}:wght@${weights}`;
        })
        .join('&');
      
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?${fontFamilies}&display=swap`;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      
      // Wait for fonts to load
      link.onload = () => {
        setFontsLoaded(true);
      };
    };
    
    loadFonts();
  }, []);

  // Calculate appropriate zoom based on format
  const calculateZoom = (format) => {
    const targetWidth = 800;
    return Math.min(targetWidth / format.width, 0.5);
  };

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (canvasRef.current && !fabricCanvasRef.current) {
      const initialZoom = calculateZoom(canvasFormat);
      
      const fabricCanvas = new Canvas(canvasRef.current, {
        width: canvasFormat.width,
        height: canvasFormat.height,
        backgroundColor: '#1a1a2e',
        preserveObjectStacking: true,
        selection: true
      });

      fabricCanvasRef.current = fabricCanvas;
      setCanvas(fabricCanvas);
      setZoom(initialZoom);

      // Event handlers
      fabricCanvas.on('selection:created', (e) => setSelectedObject(e.selected[0]));
      fabricCanvas.on('selection:updated', (e) => setSelectedObject(e.selected[0]));
      fabricCanvas.on('selection:cleared', () => setSelectedObject(null));
      fabricCanvas.on('object:modified', () => saveToHistory(fabricCanvas));

      // Load template or design - wait for canvas to be fully ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (fabricCanvasRef.current) {
            if (design?.canvas_data) {
              loadCanvasData(fabricCanvasRef.current, design.canvas_data);
            } else if (template?.canvas_data) {
              loadCanvasData(fabricCanvasRef.current, template.canvas_data);
            }
            // Force render after load
            fabricCanvasRef.current.requestRenderAll();

            // ✅ NEU: Set CSS background after load
            if (fabricCanvasRef.current.backgroundColor && fabricCanvasRef.current.lowerCanvasEl) {
              fabricCanvasRef.current.lowerCanvasEl.style.backgroundColor = fabricCanvasRef.current.backgroundColor;
            }
          }
        });
      });

      // Apply initial zoom
      updateZoom(fabricCanvas, initialZoom);

      return () => {
        fabricCanvas.dispose();
        fabricCanvasRef.current = null;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

// Load canvas data
  const loadCanvasData = (fabricCanvas, data) => {
    try {
      if (!fabricCanvas) {
        console.warn('Canvas not ready yet, skipping load');
        return;
      }
      
      const canvasData = typeof data === 'string' ? JSON.parse(data) : data;
      const bgColorFromData = canvasData.backgroundColor;
      
      try {
        fabricCanvas.loadFromJSON(canvasData, () => {
          if (bgColorFromData) {
            // ✅ FIX: Set background multiple ways for Fabric.js v6
            fabricCanvas.backgroundColor = bgColorFromData;
            fabricCanvas.set('backgroundColor', bgColorFromData);
            setBgColor(bgColorFromData);
            
            // ✅ FIX: Also set CSS background on the actual canvas element
            if (fabricCanvas.lowerCanvasEl) {
              fabricCanvas.lowerCanvasEl.style.backgroundColor = bgColorFromData;
            }
          }
          
          // ✅ FIX: Handle background image loading (Unsplash etc.)
          if (fabricCanvas.backgroundImage) {
            setBgImage('loaded');
            const bgImg = fabricCanvas.backgroundImage;
            // Check if image element exists and needs loading
            if (bgImg._element) {
              if (bgImg._element.complete) {
                // Image already loaded
                fabricCanvas.requestRenderAll();
              } else {
                // Wait for image to load
                bgImg._element.onload = () => {
                  fabricCanvas.requestRenderAll();
                };
              }
            }
          }
          
          // ✅ FIX: Also handle image objects (not just background)
          const imageObjects = fabricCanvas.getObjects().filter(obj => obj.type === 'image');
          imageObjects.forEach(imgObj => {
            if (imgObj._element && !imgObj._element.complete) {
              imgObj._element.onload = () => {
                fabricCanvas.requestRenderAll();
              };
            }
          });
          
          fabricCanvas.getObjects().forEach(obj => {
            if (obj.isDynamic) {
              updateDynamicField(obj);
            }
          });
          
          fabricCanvas.requestRenderAll();
          
          // ✅ FIX: Multiple renders to ensure images are shown
          setTimeout(() => {
            fabricCanvas.requestRenderAll();
          }, 100);
          
          setTimeout(() => {
            fabricCanvas.requestRenderAll();
          }, 500);
          
          requestAnimationFrame(() => {
            if (fabricCanvas.lowerCanvasEl && bgColorFromData) {
              fabricCanvas.lowerCanvasEl.style.backgroundColor = bgColorFromData;
            }
            fabricCanvas.requestRenderAll();
          });
          
          saveToHistory(fabricCanvas);
        });
      } catch (loadError) {
        console.warn('Canvas load error:', loadError.message);
      }
      
    } catch (error) {
      console.error('Error loading canvas data:', error);
    }
  };

  // Update dynamic fields (date, time, etc.)
  const updateDynamicField = (obj) => {
    if (!obj.isDynamic) return;

    const now = new Date();
    const formatters = {
      currentDate: () => now.toLocaleDateString('de-CH', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      }),
      currentTime: () => now.toLocaleTimeString('de-CH', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      dayOfWeek: () => now.toLocaleDateString('de-CH', { weekday: 'long' })
    };

    const text = obj.text;
    Object.entries(formatters).forEach(([key, formatter]) => {
      if (text && text.includes(`{{${key}}}`)) {
        obj.set('text', text.replace(`{{${key}}}`, formatter()));
      }
    });
  };

  // Zoom handling
  const updateZoom = (fabricCanvas, newZoom) => {
    const clampedZoom = Math.max(0.05, Math.min(2, newZoom));
    setZoom(clampedZoom);
    
    if (fabricCanvas) {
      fabricCanvas.setZoom(clampedZoom);
      fabricCanvas.setWidth(canvasFormat.width * clampedZoom);
      fabricCanvas.setHeight(canvasFormat.height * clampedZoom);
    }
  };

  // Change canvas format
  const changeFormat = (newFormat) => {
    if (!canvas) return;
    
    const oldFormat = canvasFormat;
    setCanvasFormat(newFormat);
    
    // Calculate scale factors
    const scaleX = newFormat.width / oldFormat.width;
    const scaleY = newFormat.height / oldFormat.height;
    
    // Resize canvas
    canvas.setWidth(newFormat.width);
    canvas.setHeight(newFormat.height);
    
    // Scale and reposition all objects
    canvas.getObjects().forEach(obj => {
      obj.set({
        left: obj.left * scaleX,
        top: obj.top * scaleY,
        scaleX: obj.scaleX * Math.min(scaleX, scaleY),
        scaleY: obj.scaleY * Math.min(scaleX, scaleY)
      });
      obj.setCoords();
    });
    
    // Handle background image
    if (canvas.backgroundImage) {
      const bgImg = canvas.backgroundImage;
      const newScaleX = newFormat.width / bgImg.width;
      const newScaleY = newFormat.height / bgImg.height;
      const bgScale = Math.max(newScaleX, newScaleY);
      
      bgImg.set({
        scaleX: bgScale,
        scaleY: bgScale,
        left: newFormat.width / 2,
        top: newFormat.height / 2,
        originX: 'center',
        originY: 'center'
      });
    }
    
    // Recalculate zoom
    const newZoom = calculateZoom(newFormat);
    canvas.setZoom(newZoom);
    canvas.setWidth(newFormat.width * newZoom);
    canvas.setHeight(newFormat.height * newZoom);
    setZoom(newZoom);
    
    canvas.renderAll();
    saveToHistory(canvas);
    setShowFormatModal(false);
  };

  // History management
  const saveToHistory = (fabricCanvas) => {
  const json = fabricCanvas.toJSON(['name', 'isDynamic', 'selectable', 'editable']);
  
  // ✅ Include attribution in history
  if (bgAttribution) {
    json.backgroundAttribution = bgAttribution;
  }
  
  const newHistory = history.slice(0, historyIndex + 1);
  newHistory.push(JSON.stringify(json));
  setHistory(newHistory);
  setHistoryIndex(newHistory.length - 1);
};

  const undo = useCallback(async () => {
    if (historyIndex > 0 && canvas) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      await canvas.loadFromJSON(JSON.parse(history[newIndex]));
      canvas.renderAll();
    }
  }, [canvas, history, historyIndex]);

  const redo = useCallback(async () => {
    if (historyIndex < history.length - 1 && canvas) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      await canvas.loadFromJSON(JSON.parse(history[newIndex]));
      canvas.renderAll();
    }
  }, [canvas, history, historyIndex]);

  // Add text
  const addText = () => {
    if (!canvas) return;
    
    const text = new Textbox(t('contentStudio.newText', 'Neuer Text'), {
      left: 100,
      top: 100,
      fontSize: 48,
      fontFamily: 'Montserrat',
      fill: '#ffffff',
      width: 400,
      editable: true
    });
    
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    saveToHistory(canvas);
  };

  // Add shape
  const addShape = (type) => {
    if (!canvas) return;
    
    let shape;
    const commonProps = {
      left: 100,
      top: 100,
      fill: '#e94560',
      stroke: null,
      strokeWidth: 0
    };

    switch (type) {
      case 'rect':
        shape = new Rect({ ...commonProps, width: 200, height: 100, rx: 8, ry: 8 });
        break;
      case 'circle':
        shape = new Circle({ ...commonProps, radius: 75 });
        break;
      default:
        return;
    }
    
    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.renderAll();
    saveToHistory(canvas);
  };

  // Add image
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !canvas) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const img = await FabricImage.fromURL(event.target.result);
        
        // Scale image to fit
        const maxWidth = 800;
        const maxHeight = 600;
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
        
        img.set({
          left: 100,
          top: 100,
          scaleX: scale,
          scaleY: scale
        });
        
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        saveToHistory(canvas);
      } catch (error) {
        console.error('Error loading image:', error);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Set background image from file upload
  const handleBgImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !canvas) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      await setBackgroundFromUrl(event.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Set background image from URL (used by gallery and file upload)
const setBackgroundFromUrl = async (imageUrl) => {
  if (!canvas) return;
  
  try {
    // ✅ FIX: Use window.Image (not lucide-react Image!)
    const imgElement = document.createElement('img');
    imgElement.crossOrigin = 'anonymous';
    
    await new Promise((resolve, reject) => {
      imgElement.onload = resolve;
      imgElement.onerror = reject;
      imgElement.src = imageUrl;
    });
    
    // Create FabricImage from loaded element
    const img = new FabricImage(imgElement);
    
    const scaleX = canvasFormat.width / img.width;
    const scaleY = canvasFormat.height / img.height;
    const scale = Math.max(scaleX, scaleY);
    
    img.scaleX = scale;
    img.scaleY = scale;
    
    // Set as background
    canvas.backgroundImage = img;
    canvas.renderAll();
    
    setBgImage(imageUrl);
    saveToHistory(canvas);
  } catch (error) {
    console.error('Error loading background image:', error);
  }
};

// Handle gallery selection
const handleGallerySelect = async (imageUrl, imageData) => {
  console.log('🖼️ Gallery select:', imageUrl);
  
  // ✅ CRITICAL: Remove ALL images BEFORE setting background
  if (canvas) {
    canvas.getObjects('image').forEach(obj => canvas.remove(obj));
    canvas.renderAll();
  }
  
  await setBackgroundFromUrl(imageUrl);
  
  // ✅ CRITICAL: Remove ALL images AFTER setting background  
  setTimeout(() => {
    if (canvas) {
      canvas.getObjects('image').forEach(obj => canvas.remove(obj));
      canvas.renderAll();
    }
  }, 50);
  
  if (imageData?.source === 'unsplash' && imageData?.attribution) {
    setBgAttribution(imageData.attribution);
  } else {
    setBgAttribution(null);
  }
  
  setShowBgGalleryModal(false);
};

  // Remove background image
  const removeBgImage = () => {
  if (!canvas) return;
  canvas.backgroundImage = null;
  canvas.renderAll();
  setBgImage(null);
  setBgAttribution(null); // ✅ Clear attribution
  saveToHistory(canvas);
};

  // Delete selected
  const deleteSelected = useCallback(() => {
    if (!canvas || !selectedObject) return;
    canvas.remove(selectedObject);
    setSelectedObject(null);
    canvas.renderAll();
    saveToHistory(canvas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, selectedObject]);

  // Duplicate selected
  const duplicateSelected = useCallback(async () => {
    if (!canvas || !selectedObject) return;
    
    const cloned = await selectedObject.clone();
    cloned.set({
      left: selectedObject.left + 20,
      top: selectedObject.top + 20
    });
    canvas.add(cloned);
    canvas.setActiveObject(cloned);
    canvas.renderAll();
    saveToHistory(canvas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, selectedObject]);

  // Layer controls
  const bringForward = () => {
    if (!canvas || !selectedObject) return;
    canvas.bringObjectForward(selectedObject);
    canvas.renderAll();
  };

  const sendBackward = () => {
    if (!canvas || !selectedObject) return;
    canvas.sendObjectBackwards(selectedObject);
    canvas.renderAll();
  };

  // Text formatting
  const updateTextProperty = (property, value) => {
    if (!canvas || !selectedObject || selectedObject.type !== 'textbox') return;
    selectedObject.set(property, value);
    canvas.renderAll();
    saveToHistory(canvas);
  };

  const toggleBold = () => {
    const current = selectedObject?.fontWeight;
    updateTextProperty('fontWeight', current === 'bold' || current >= 700 ? 'normal' : 'bold');
  };

  const toggleItalic = () => {
    const current = selectedObject?.fontStyle;
    updateTextProperty('fontStyle', current === 'italic' ? 'normal' : 'italic');
  };

  const toggleUnderline = () => {
    const current = selectedObject?.underline;
    updateTextProperty('underline', !current);
  };

  // Change font family
  const changeFontFamily = (fontName) => {
    updateTextProperty('fontFamily', fontName);
    setShowFontModal(false);
  };

  // Save design
const handleSave = useCallback(async () => {
  if (!canvas) return;
  
  setSaving(true);
  setSaveSuccess(false);
  try {
    const currentBgColor = canvas.backgroundColor;
    const canvasData = canvas.toJSON(['name', 'isDynamic', 'selectable', 'editable']);
    
    const finalBgColor = currentBgColor || bgColor || '#1a1a2e';
    canvasData.backgroundColor = finalBgColor;
    
    // ✅ Include background attribution if present
    if (bgAttribution) {
      canvasData.backgroundAttribution = bgAttribution;
    }
    
    // Create offscreen canvas for thumbnail (no flicker)
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = 400;
    offscreenCanvas.height = Math.round(400 * canvasFormat.height / canvasFormat.width);
    const offscreenCtx = offscreenCanvas.getContext('2d');
    
    // Draw background
    offscreenCtx.fillStyle = finalBgColor;
    offscreenCtx.fillRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
    
    // Draw current canvas scaled down
    const mainCanvas = canvas.getElement();
    offscreenCtx.drawImage(
      mainCanvas, 
      0, 0, mainCanvas.width, mainCanvas.height,
      0, 0, offscreenCanvas.width, offscreenCanvas.height
    );
    
    const thumbnail = offscreenCanvas.toDataURL('image/jpeg', 0.8);
    
    const result = await onSave({
      name: designName,
      canvasData: canvasData,
      thumbnail: thumbnail,
      width: canvasFormat.width,
      height: canvasFormat.height,
      format: canvasFormat.id
    });
    
    if (result) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    }
  } catch (error) {
    console.error('Error saving design:', error);
  } finally {
    setSaving(false);
  }
}, [canvas, designName, canvasFormat, onSave, bgColor, bgAttribution]); // ✅ bgAttribution zu dependencies hinzugefügt

 // Export as image
  const exportAsImage = useCallback((format = 'png') => {
    if (!canvas) return;
    
    // Get background color with fallback
    const currentBgColor = canvas.backgroundColor || bgColor || '#1a1a2e';
    
    // Store current zoom
    const currentZoom = canvas.getZoom();
    
    // Reset for export
    canvas.setZoom(1);
    canvas.setWidth(canvasFormat.width);
    canvas.setHeight(canvasFormat.height);
    canvas.backgroundColor = currentBgColor;
    canvas.requestRenderAll();
    
    // For 4K, we don't need multiplier. For others, use 2x for better quality
    const multiplier = canvasFormat.width >= 3840 ? 1 : 2;
    
    const dataUrl = canvas.toDataURL({
      format: format,
      quality: 0.95,
      multiplier: multiplier
    });
    
    // Download
    const link = document.createElement('a');
    link.download = `${designName}.${format}`;
    link.href = dataUrl;
    link.click();
    
    // ✅ FIX: Restore zoom and calculate dimensions correctly
    canvas.setZoom(currentZoom);
    canvas.setWidth(canvasFormat.width * currentZoom);
    canvas.setHeight(canvasFormat.height * currentZoom);
    canvas.backgroundColor = currentBgColor;
    if (canvas.lowerCanvasEl) {
      canvas.lowerCanvasEl.style.backgroundColor = currentBgColor;
    }
    canvas.requestRenderAll();
  }, [canvas, canvasFormat, designName, bgColor]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) redo();
            else undo();
            break;
          case 's':
            e.preventDefault();
            handleSave();
            break;
          case 'd':
            e.preventDefault();
            duplicateSelected();
            break;
          default:
            break;
        }
      }
      
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedObject && !selectedObject.isEditing) {
          e.preventDefault();
          deleteSelected();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvas, selectedObject, undo, redo, handleSave, duplicateSelected, deleteSelected]);

  return (
    <div className="fixed inset-0 bg-gray-100 dark:bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ChevronLeft size={24} className="text-gray-600 dark:text-gray-400" />
          </button>
          
          <input
            type="text"
            value={designName}
            onChange={(e) => setDesignName(e.target.value)}
            className="text-lg font-semibold bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white"
            placeholder={t('contentStudio.designName', 'Design Name')}
          />

          {/* Format Button */}
          <button
            onClick={() => setShowFormatModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <Monitor size={16} />
            {canvasFormat.label}
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <button
            onClick={undo}
            disabled={historyIndex <= 0}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title={t('contentStudio.undo', 'Rückgängig (Ctrl+Z)')}
          >
            <Undo size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title={t('contentStudio.redo', 'Wiederherstellen (Ctrl+Shift+Z)')}
          >
            <Redo size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-2" />

          {/* Zoom */}
          <button
            onClick={() => updateZoom(canvas, zoom - 0.1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ZoomOut size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[50px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => updateZoom(canvas, zoom + 0.1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ZoomIn size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-2" />

          {/* Export */}
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Download size={20} />
            <span className="hidden md:inline">{t('contentStudio.export', 'Exportieren')}</span>
          </button>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              saveSuccess 
                ? 'bg-green-600 text-white' 
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {saving ? (
              <Loader size={20} className="animate-spin" />
            ) : saveSuccess ? (
              <Check size={20} />
            ) : (
              <Save size={20} />
            )}
            <span className="hidden md:inline">
              {saving ? t('common.saving', 'Speichern...') : saveSuccess ? t('common.saved', 'Gespeichert!') : t('common.save', 'Speichern')}
            </span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Toolbar */}
        <div className="w-16 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col items-center py-4 gap-2">
          <button
            onClick={addText}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group"
            title={t('contentStudio.addText', 'Text hinzufügen')}
          >
            <Type size={24} className="text-gray-600 dark:text-gray-400 group-hover:text-purple-600" />
          </button>
          
          <button
            onClick={() => addShape('rect')}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group"
            title={t('contentStudio.addRectangle', 'Rechteck hinzufügen')}
          >
            <Square size={24} className="text-gray-600 dark:text-gray-400 group-hover:text-purple-600" />
          </button>
          
          <button
            onClick={() => addShape('circle')}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group"
            title={t('contentStudio.addCircle', 'Kreis hinzufügen')}
          >
            <CircleIcon size={24} className="text-gray-600 dark:text-gray-400 group-hover:text-purple-600" />
          </button>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group"
            title={t('contentStudio.addImage', 'Bild hinzufügen')}
          >
            <Image size={24} className="text-gray-600 dark:text-gray-400 group-hover:text-purple-600" />
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />

          <div className="flex-1" />

          {selectedObject && (
            <>
              <button
                onClick={duplicateSelected}
                className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={t('contentStudio.duplicate', 'Duplizieren (Ctrl+D)')}
              >
                <Copy size={24} className="text-gray-600 dark:text-gray-400" />
              </button>
              
              <button
                onClick={deleteSelected}
                className="p-3 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title={t('contentStudio.delete', 'Löschen (Delete)')}
              >
                <Trash2 size={24} className="text-red-500" />
              </button>
            </>
          )}
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto bg-gray-200 dark:bg-gray-900 p-8 flex items-center justify-center">
          <div className="shadow-2xl">
            <canvas ref={canvasRef} />
          </div>
        </div>

        {/* Right Properties Panel - Always visible */}
<div className="w-85 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
  
  {/* AI Settings - NEW */}
  <AISettings />

  {/* Canvas Background - always visible */}
  <div className="mb-6">
    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
      {t('contentStudio.canvas', 'Canvas')}
    </h3>

    {/* AI Assistant Enhanced - REPLACED */}
    <AIAssistantEnhanced 
      currentText={selectedObject?.text}
      onContentGenerated={(content) => {
        console.log('🎨 AI Content received:', content);
        
        if (!canvas) {
          console.error('❌ Canvas not available');
          return;
        }
        
        if (selectedObject && selectedObject.type === 'textbox') {
          console.log('✅ Updating selected textbox...');
          selectedObject.set('text', content);
          canvas.renderAll();
          saveToHistory(canvas);
        } else {
          console.log('✅ Creating new text with addText()...');
          addText();
          
          setTimeout(() => {
            const activeObj = canvas.getActiveObject();
            if (activeObj && activeObj.type === 'textbox') {
              activeObj.set('text', content);
              canvas.renderAll();
              saveToHistory(canvas);
              console.log('✅ Text set successfully!');
            }
          }, 50);
        }
      }}
    />

    {/* Translation Panel Enhanced - REPLACED */}
    <TranslationPanelEnhanced
      design={canvas ? { canvas_data: canvas.toJSON() } : null}
      currentText={selectedObject?.text}
      onTranslated={(translated) => {
        console.log('🌍 Translation received:', translated);
        
        if (!canvas) {
          console.error('❌ Canvas not available');
          return;
        }
        
        if (typeof translated === 'string') {
          if (selectedObject && selectedObject.type === 'textbox') {
            console.log('✅ Translating selected textbox...');
            selectedObject.set('text', translated);
            canvas.renderAll();
            saveToHistory(canvas);
            console.log('✅ Translation applied!');
          } else {
            console.warn('⚠️ No textbox selected, creating new one');
            addText();
            setTimeout(() => {
              const activeObj = canvas.getActiveObject();
              if (activeObj && activeObj.type === 'textbox') {
                activeObj.set('text', translated);
                canvas.renderAll();
                saveToHistory(canvas);
              }
            }, 50);
          }
        } else if (translated && translated.canvas_data) {
          console.log('✅ Loading translated design...');
          console.log('Translated canvas_data:', translated.canvas_data);
          loadCanvasData(canvas, translated.canvas_data);
        }
      }}
      onDebug={() => {
        // Debug function to check canvas data
        if (canvas) {
          const canvasData = canvas.toJSON();
          console.log('🐛 DEBUG Canvas Data:', canvasData);
          console.log('🐛 Objects count:', canvasData.objects?.length || 0);
          console.log('🐛 Objects:', canvasData.objects);
        }
      }}
    />
    
    {/* Format Info */}
    <div className="mb-4 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {canvasFormat.label} ({canvasFormat.description})
      </p>
    </div>
    
    {/* Background Color */}
    <div className="mb-4">
      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
        {t('contentStudio.backgroundColor', 'Hintergrundfarbe')}
      </label>
      <input
        type="color"
        value={bgColor}
        onChange={(e) => {
          setBgColor(e.target.value);
          if (canvas) {
            canvas.set('backgroundColor', e.target.value);
            canvas.renderAll();
            saveToHistory(canvas);
          }
        }}
        className="w-full h-10 rounded-lg cursor-pointer"
      />
    </div>

    {/* Background Image */}
    <div>
      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
        {t('contentStudio.backgroundImage', 'Hintergrundbild')}
      </label>
      
      {bgImage ? (
  <div className="space-y-2">
    <div className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
      <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm">
        {t('contentStudio.backgroundSet', 'Hintergrund gesetzt')}
      </div>
    </div>
    <div className="flex gap-2">
      <button
        onClick={() => setShowBgGalleryModal(true)}
        className="flex-1 py-2 text-sm text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
      >
        {t('contentStudio.changeBackground', 'Ändern')}
      </button>
      <button
        onClick={removeBgImage}
        className="flex-1 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
      >
        {t('contentStudio.removeBackground', 'Entfernen')}
      </button>
    </div>
  </div>
) : (
  <div className="space-y-2">

          {/* Unsplash Attribution Display */}
          {bgAttribution && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                📷 {t('contentStudio.attribution', 'Attribution')}
              </p>
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                Photo by{' '}
                <a
                  href={bgAttribution.photographerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline font-medium"
                >
                  {bgAttribution.photographerName}
                </a>
                {' '}on{' '}
                <a
                  href={bgAttribution.unsplashUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline font-medium"
                >
                  Unsplash
                </a>
              </p>
            </div>
          )}

          {/* Gallery Button */}
          <button
            onClick={() => setShowBgGalleryModal(true)}
            className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:border-purple-500 hover:text-purple-500 transition-colors flex items-center justify-center gap-2"
          >
            <ImagePlus size={18} />
            {t('contentStudio.browseGallery', 'Galerie durchsuchen')}
          </button>
          
          {/* Upload Button */}
          <button
            onClick={() => bgImageInputRef.current?.click()}
            className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-purple-500 transition-colors flex items-center justify-center gap-2"
          >
            <Upload size={16} />
            {t('contentStudio.uploadOwn', 'Eigenes hochladen')}
          </button>
        </div>
      )}
      
      <input
        ref={bgImageInputRef}
        type="file"
        accept="image/*"
        onChange={handleBgImageUpload}
        className="hidden"
      />
    </div>
  </div>

  {/* Object Properties - only when selected */}
  {selectedObject && (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
        {t('contentStudio.properties', 'Eigenschaften')}
      </h3>

      {/* Text properties */}
      {selectedObject.type === 'textbox' && (
        <div className="space-y-4">
          {/* Font Family */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.fontFamily', 'Schriftart')}
            </label>
            <button
              onClick={() => setShowFontModal(true)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-left hover:border-purple-500 transition-colors"
              style={{ fontFamily: selectedObject.fontFamily }}
            >
              {selectedObject.fontFamily || 'Montserrat'}
            </button>
          </div>

          {/* Font size */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.fontSize', 'Schriftgrösse')}
            </label>
            <input
              type="number"
              value={selectedObject.fontSize || 24}
              onChange={(e) => updateTextProperty('fontSize', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          {/* Text formatting */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.formatting', 'Formatierung')}
            </label>
            <div className="flex gap-1">
              <button
                onClick={toggleBold}
                className={`p-2 rounded-lg transition-colors ${
                  selectedObject.fontWeight === 'bold' || selectedObject.fontWeight >= 700
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Bold size={18} />
              </button>
              <button
                onClick={toggleItalic}
                className={`p-2 rounded-lg transition-colors ${
                  selectedObject.fontStyle === 'italic' 
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Italic size={18} />
              </button>
              <button
                onClick={toggleUnderline}
                className={`p-2 rounded-lg transition-colors ${
                  selectedObject.underline 
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Underline size={18} />
              </button>
            </div>
          </div>

          {/* Text alignment */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.alignment', 'Ausrichtung')}
            </label>
            <div className="flex gap-1">
              <button
                onClick={() => updateTextProperty('textAlign', 'left')}
                className={`p-2 rounded-lg transition-colors ${
                  selectedObject.textAlign === 'left' 
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <AlignLeft size={18} />
              </button>
              <button
                onClick={() => updateTextProperty('textAlign', 'center')}
                className={`p-2 rounded-lg transition-colors ${
                  selectedObject.textAlign === 'center' 
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <AlignCenter size={18} />
              </button>
              <button
                onClick={() => updateTextProperty('textAlign', 'right')}
                className={`p-2 rounded-lg transition-colors ${
                  selectedObject.textAlign === 'right' 
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <AlignRight size={18} />
              </button>
            </div>
          </div>

          {/* Text color */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.textColor', 'Textfarbe')}
            </label>
            <input
              type="color"
              value={selectedObject.fill || '#ffffff'}
              onChange={(e) => updateTextProperty('fill', e.target.value)}
              className="w-full h-10 rounded-lg cursor-pointer"
            />
          </div>
        </div>
      )}

      {/* Shape properties */}
      {(selectedObject.type === 'rect' || selectedObject.type === 'circle') && (
        <div className="space-y-4">
          {/* Fill color */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.fillColor', 'Füllfarbe')}
            </label>
            <input
              type="color"
              value={selectedObject.fill || '#e94560'}
              onChange={(e) => {
                selectedObject.set('fill', e.target.value);
                canvas.renderAll();
                saveToHistory(canvas);
              }}
              className="w-full h-10 rounded-lg cursor-pointer"
            />
          </div>

          {/* Opacity */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              {t('contentStudio.opacity', 'Transparenz')}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={selectedObject.opacity || 1}
              onChange={(e) => {
                selectedObject.set('opacity', parseFloat(e.target.value));
                canvas.renderAll();
                saveToHistory(canvas);
              }}
              className="w-full"
            />
          </div>
        </div>
      )}

      {/* Layer controls */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
          {t('contentStudio.layer', 'Ebene')}
        </label>
        <div className="flex gap-2">
          <button
            onClick={bringForward}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
          >
            <ChevronUp size={16} />
            {t('contentStudio.forward', 'Vor')}
          </button>
          <button
            onClick={sendBackward}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
          >
            <ChevronDown size={16} />
            {t('contentStudio.backward', 'Zurück')}
          </button>
        </div>
      </div>
    </div>
  )}
</div>
</div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          designName={designName}
          canvasFormat={canvasFormat}
          onExport={exportAsImage}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Background Gallery Modal */}
      {showBgGalleryModal && (
        <BackgroundGalleryModal
          onSelect={handleGallerySelect}
          onClose={() => setShowBgGalleryModal(false)}
        />
      )}

      {/* Format Selection Modal */}
      {showFormatModal && (
        <FormatModal
          currentFormat={canvasFormat}
          formats={CANVAS_FORMATS}
          onSelect={changeFormat}
          onClose={() => setShowFormatModal(false)}
        />
      )}

      {/* Font Selection Modal */}
      {showFontModal && (
        <FontModal
          currentFont={selectedObject?.fontFamily || 'Montserrat'}
          categories={FONT_CATEGORIES}
          onSelect={changeFontFamily}
          onClose={() => setShowFontModal(false)}
        />
      )}
    </div>
  );
};

// Export Modal Component
const ExportModal = ({ designName, canvasFormat, onExport, onClose }) => {
  const { t } = useTranslation();
  const [format, setFormat] = useState('png');

  const getExportResolution = () => {
    if (canvasFormat.width >= 3840) {
      return `${canvasFormat.width} × ${canvasFormat.height}`;
    }
    return `${canvasFormat.width * 2} × ${canvasFormat.height * 2}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('contentStudio.exportDesign', 'Design exportieren')}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
              {t('contentStudio.format', 'Format')}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setFormat('png')}
                className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                  format === 'png'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                PNG
              </button>
              <button
                onClick={() => setFormat('jpeg')}
                className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                  format === 'jpeg'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                JPEG
              </button>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>{t('contentStudio.canvasSize', 'Canvas')}:</strong> {canvasFormat.label}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              <strong>{t('contentStudio.resolution', 'Auflösung')}:</strong> {getExportResolution()}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              <strong>{t('contentStudio.filename', 'Dateiname')}:</strong> {designName}.{format}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            onClick={() => {
              onExport(format);
              onClose();
            }}
            className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Download size={18} />
            {t('contentStudio.download', 'Herunterladen')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Format Selection Modal
const FormatModal = ({ currentFormat, formats, onSelect, onClose }) => {
  const { t } = useTranslation();

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('contentStudio.selectFormat', 'Format auswählen')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('contentStudio.formatWarning', 'Inhalte werden automatisch angepasst')}
        </p>

        <div className="grid grid-cols-1 gap-3">
          {formats.map((format) => {
            const Icon = format.icon;
            const isActive = currentFormat.id === format.id;
            
            return (
              <button
                key={format.id}
                onClick={() => onSelect(format)}
                className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                  isActive
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                }`}
              >
                <div className={`p-3 rounded-lg ${isActive ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-gray-100 dark:bg-gray-700'}`}>
                  <Icon size={24} className={isActive ? 'text-purple-600' : 'text-gray-600 dark:text-gray-400'} />
                </div>
                <div className="flex-1 text-left">
                  <p className={`font-medium ${isActive ? 'text-purple-600' : 'text-gray-900 dark:text-white'}`}>
                    {format.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format.description}
                  </p>
                </div>
                {isActive && (
                  <Check size={20} className="text-purple-600" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {t('common.close', 'Schliessen')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Font Selection Modal
const FontModal = ({ currentFont, categories, onSelect, onClose }) => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('headlines');

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('contentStudio.selectFont', 'Schriftart auswählen')}
          </h3>
        </div>

        {/* Category Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6 overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === category.id
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t(category.nameKey, category.name)}
            </button>
          ))}
        </div>

        {/* Font List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-2">
            {categories
              .find(c => c.id === activeCategory)
              ?.fonts.map((font) => {
                const isActive = currentFont === font.name;
                
                return (
                  <button
                    key={font.name}
                    onClick={() => onSelect(font.name)}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                      isActive
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                    }`}
                  >
                    <div className="text-left">
                      <p 
                        className="text-2xl text-gray-900 dark:text-white"
                        style={{ fontFamily: font.name }}
                      >
                        {font.name}
                      </p>
                      <p 
                        className="text-sm text-gray-500 dark:text-gray-400 mt-1"
                        style={{ fontFamily: font.name }}
                      >
                        The quick brown fox jumps over the lazy dog
                      </p>
                    </div>
                    {isActive && (
                      <Check size={20} className="text-purple-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {t('common.close', 'Schliessen')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CanvasEditor;